import { Component, ChangeDetectionStrategy, inject, signal, effect, computed, DestroyRef } from '@angular/core';
import { CommonModule, DOCUMENT } from '@angular/common';
import { HttpClient } from '@angular/common/http';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import {
  AlertComponent,
  CardBodyComponent,
  CardComponent,
  CardHeaderComponent,
  ColComponent,
  ContainerComponent,
  RowComponent,
  SpinnerComponent
} from '@coreui/angular-pro';
import { AuthService } from 'auth';
import { TranslatePipe } from 'i18n';
import { ActivatedRoute } from '@angular/router';

@Component({
  selector: 'app-kyc-sumsub-page',
  standalone: true,
  imports: [
    CommonModule,
    AlertComponent,
    CardComponent,
    CardBodyComponent,
    CardHeaderComponent,
    ContainerComponent,
    RowComponent,
    ColComponent,
    SpinnerComponent,
    TranslatePipe
  ],
  templateUrl: './kyc-sumsub-page.component.html',
  styles: [`
    :host {
      display: block;
      position: absolute;
      inset: 0;
      overflow-y: auto;
    }
  `],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class KycSumsubPageComponent {
  private http = inject(HttpClient);
  private auth = inject(AuthService);
  private route = inject(ActivatedRoute);
  private document = inject(DOCUMENT);
  private destroyRef = inject(DestroyRef);

  loading = signal(true);
  submitted = signal(false);
  error = signal<string | null>(null);

  private sdkScriptId = 'sumsub-websdk-script';
  private sdkInstance: any = null;

  lang = computed(() => this.route.snapshot.parent?.params?.['lang'] || 'en');

  constructor() {
    // Load SDK → start verification
    this.loadSumsubSdk()
      .then(() => this.startVerification())
      .catch(() => {
        this.loading.set(false);
        this.error.set('Failed to load verification SDK.');
      });
  }

  private loadSumsubSdk(): Promise<void> {
    return new Promise((resolve, reject) => {
      // Already loaded?
      if ((window as any).snsWebSdk) {
        resolve();
        return;
      }

      // Script already in DOM?
      const existing = this.document.getElementById(this.sdkScriptId);
      if (existing) {
        existing.addEventListener('load', () => resolve());
        existing.addEventListener('error', () => reject());
        return;
      }

      // Create & append script
      const script = this.document.createElement('script');
      script.id = this.sdkScriptId;
      script.src = 'https://static.sumsub.com/idensic/static/sns-websdk-builder.js';
      script.onload = () => resolve();
      script.onerror = () => reject();
      this.document.head.appendChild(script);
    });
  }

  private startVerification(): void {
    this.http.post<{ token: string; applicant_id: string }>('/kyc/start_sumsub', {})
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: ({ token }) => {
          this.loading.set(false);
          this.initSdk(token);
        },
        error: (err) => {
          this.loading.set(false);
          this.error.set(err.error?.error || 'Could not start verification.');
        }
      });
  }

  private initSdk(token: string): void {
    const sdkLang = this.lang() === 'de' ? 'de' : 'en';

    this.sdkInstance = (window as any).snsWebSdk
      .init(token, () => this.refreshToken())
      .withConf({ lang: sdkLang, theme: 'dark' })
      .withOptions({ addViewportTag: false, adaptIframeHeight: true })
      .on('idCheck.onApplicantSubmitted', () => {
        this.submitted.set(true);
        this.auth.refreshUser().pipe(takeUntilDestroyed(this.destroyRef)).subscribe();
      })
      .on('idCheck.onError', (err: any) => {
        this.error.set(err?.error || 'Verification error.');
      })
      .build();

    // Launch SDK — container should already exist in DOM
    this.sdkInstance.launch('#sumsub-websdk-container');
  }

  private refreshToken(): Promise<string> {
    return new Promise((resolve, reject) => {
      this.http.post<{ token: string }>('/kyc/start_sumsub', {})
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe({
          next: ({ token }) => resolve(token),
          error: () => reject(new Error('Token refresh failed'))
        });
    });
  }
}